/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static targets = [
    "loanAmount",
    "interestRate",
    "loanTerm",
    "startDate",
    "paymentsToDateChart",
    "thisMonthChart",
    "futurePaymentsChart",
    "numberOfPayments",
    "monthlyPayment",
    "totalInterest",
    "totalCost",
    "payoffDate",
    "paidTowardsLoan",
    "paidTowardsInterest",
    "thisMonthPrincipal",
    "thisMonthInterestAmount",
    "futurePrincipal",
    "futureInterest",
    "equityPercentage",
    "tableBody",
    "modalContent",
    "futureDate",
  ];

  static values = {
    loanAmount: Number,
    interestRate: Number,
    loanTerm: Number,
    startDate: String,
    maxRows: { type: Number, default: 8 },
  };

  connect() {
    this.initializeAllValues();
    this.setupEventListeners();
  }

  calculateFuturePayments(
    loanAmount,
    monthlyPayment,
    monthlyInterestRate,
    loanTerm,
    monthsElapsed,
  ) {
    const futureMonth = monthsElapsed + 36;
    let futurePrincipal, futureInterest, equityPercentage;

    if (futureMonth >= loanTerm) {
      futurePrincipal = loanAmount;
      futureInterest = monthlyPayment * loanTerm - loanAmount;
      equityPercentage = 100;
    } else {
      const futureBalance = this.calculateRemainingBalance(
        loanAmount,
        monthlyInterestRate,
        loanTerm,
        futureMonth,
      );

      futurePrincipal = loanAmount - futureBalance;
      futureInterest = monthlyPayment * futureMonth - futurePrincipal;

      const futureMonthInterest = futureBalance * monthlyInterestRate;
      const futureMonthPrincipal = monthlyPayment - futureMonthInterest;
      equityPercentage = (futureMonthPrincipal / monthlyPayment) * 100;
    }

    return { futurePrincipal, futureInterest, equityPercentage };
  }

  initializeAllValues() {
    const loanAmount = this.parseCurrency(this.loanAmountTarget.value);
    const interestRate = parseFloat(String(this.interestRateTarget.value));
    const loanTerm = parseInt(String(this.loanTermTarget.value));
    const startDate = new Date(String(this.startDateTarget.value));
    const currentDate = new Date();

    const monthlyInterestRate = interestRate / 12 / 100;
    const monthlyPayment = this.calculateMonthlyPayment(
      loanAmount,
      monthlyInterestRate,
      loanTerm,
    );

    const yearDiff = currentDate.getFullYear() - startDate.getFullYear();
    const monthDiff = currentDate.getMonth() - startDate.getMonth();
    const monthsElapsed = Math.max(0, yearDiff * 12 + monthDiff);

    const remainingBalance = this.calculateRemainingBalance(
      loanAmount,
      monthlyInterestRate,
      loanTerm,
      monthsElapsed,
    );

    const paidTowardsLoan = loanAmount - remainingBalance;
    const paidTowardsInterest =
      monthlyPayment * monthsElapsed - paidTowardsLoan;

    let thisMonthInterest;
    if (monthsElapsed > 0) {
      const previousBalance = this.calculateRemainingBalance(
        loanAmount,
        monthlyInterestRate,
        loanTerm,
        monthsElapsed - 1,
      );
      thisMonthInterest = previousBalance * monthlyInterestRate;
    } else {
      thisMonthInterest = loanAmount * monthlyInterestRate;
    }
    const thisMonthPrincipal = monthlyPayment - thisMonthInterest;

    const { futurePrincipal, futureInterest, equityPercentage } =
      this.calculateFuturePayments(
        loanAmount,
        monthlyPayment,
        monthlyInterestRate,
        loanTerm,
        monthsElapsed,
      );

    const payoffDate = new Date(startDate);
    payoffDate.setMonth(payoffDate.getMonth() + loanTerm);

    const totalInterest = monthlyPayment * loanTerm - loanAmount;
    const totalCost = loanAmount + totalInterest;

    const futureDate = new Date(currentDate);
    futureDate.setFullYear(currentDate.getFullYear() + 3);
    const futureDateTitles = this.futureDateTarget;
    futureDateTitles.textContent = futureDate.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });

    this.monthlyPayment = monthlyPayment;
    this.totalInterest = totalInterest;
    this.totalCost = totalCost;
    this.payoffDate = payoffDate;
    this.paidTowardsLoan = paidTowardsLoan;
    this.paidTowardsInterest = paidTowardsInterest;
    this.thisMonthPrincipal = thisMonthPrincipal;
    this.thisMonthInterest = thisMonthInterest;
    this.futurePrincipal = futurePrincipal;
    this.futureInterest = futureInterest;
    this.equityPercentage = equityPercentage;

    this.initializeUI();
  }

  initializeUI() {
    this.updateAllTargets("numberOfPayments", this.loanTermTarget.value);
    this.updateAllTargets(
      "monthlyPayment",
      this.formatCurrency(this.monthlyPayment),
    );
    this.updateAllTargets(
      "totalInterest",
      this.formatCurrency(this.totalInterest),
    );
    this.updateAllTargets("totalCost", this.formatCurrency(this.totalCost));
    this.updateAllTargets("payoffDate", this.formatDate(this.payoffDate));

    this.updateAllTargets(
      "paidTowardsLoan",
      this.formatCurrency(this.paidTowardsLoan),
    );
    this.updateAllTargets(
      "paidTowardsInterest",
      this.formatCurrency(this.paidTowardsInterest),
    );

    this.updateAllTargets(
      "thisMonthPrincipal",
      this.formatCurrency(this.thisMonthPrincipal),
    );
    if (this.hasThisMonthInterestAmountTarget) {
      this.updateAllTargets(
        "thisMonthInterestAmount",
        this.formatCurrency(this.thisMonthInterest),
      );
    }

    this.updateAllTargets(
      "futurePrincipal",
      this.formatCurrency(this.futurePrincipal),
    );
    this.updateAllTargets(
      "futureInterest",
      this.formatCurrency(this.futureInterest),
    );
    this.updateAllTargets(
      "equityPercentage",
      this.equityPercentage.toFixed(1) + "%",
    );

    this.initializeCharts();
    this.updateAmortizationTable();
  }

  setupEventListeners() {
    this.loanAmountTarget.addEventListener("input", () => {
      this.recalculateAll();
    });
    this.interestRateTarget.addEventListener("input", () => {
      this.recalculateAll();
    });
    this.loanTermTarget.addEventListener("change", () => {
      this.recalculateAll();
    });
    this.startDateTarget.addEventListener("change", () => {
      this.recalculateAll();
    });
  }

  recalculateAll() {
    const loanAmount = this.parseCurrency(this.loanAmountTarget.value);
    const interestRate = parseFloat(this.interestRateTarget.value);
    const loanTerm = parseInt(this.loanTermTarget.value);
    const startDate = new Date(this.startDateTarget.value);
    const currentDate = new Date();

    const monthlyInterestRate = interestRate / 12 / 100;

    const monthlyPayment = this.calculateMonthlyPayment(
      loanAmount,
      monthlyInterestRate,
      loanTerm,
    );

    const yearDiff = currentDate.getFullYear() - startDate.getFullYear();
    const monthDiff = currentDate.getMonth() - startDate.getMonth();
    const monthsElapsed = Math.max(0, yearDiff * 12 + monthDiff);

    const remainingBalance = this.calculateRemainingBalance(
      loanAmount,
      monthlyInterestRate,
      loanTerm,
      monthsElapsed,
    );

    const paidTowardsLoan = loanAmount - remainingBalance;
    const paidTowardsInterest =
      monthlyPayment * monthsElapsed - paidTowardsLoan;

    let thisMonthInterest;
    if (monthsElapsed > 0) {
      const previousBalance = this.calculateRemainingBalance(
        loanAmount,
        monthlyInterestRate,
        loanTerm,
        monthsElapsed - 1,
      );
      thisMonthInterest = previousBalance * monthlyInterestRate;
    } else {
      thisMonthInterest = loanAmount * monthlyInterestRate;
    }
    const thisMonthPrincipal = monthlyPayment - thisMonthInterest;

    const { futurePrincipal, futureInterest, equityPercentage } =
      this.calculateFuturePayments(
        loanAmount,
        monthlyPayment,
        monthlyInterestRate,
        loanTerm,
        monthsElapsed,
      );

    const payoffDate = new Date(startDate);
    payoffDate.setMonth(payoffDate.getMonth() + loanTerm);

    const totalInterest = monthlyPayment * loanTerm - loanAmount;
    const totalCost = loanAmount + totalInterest;

    const futureDate = new Date(currentDate);
    futureDate.setFullYear(currentDate.getFullYear() + 3);
    const futureDateTitles = this.futureDateTarget;
    futureDateTitles.textContent = futureDate.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });

    this.updateUI({
      monthlyPayment,
      totalInterest,
      totalCost,
      payoffDate,
      loanTerm,
      paidTowardsLoan,
      paidTowardsInterest,
      thisMonthPrincipal,
      thisMonthInterest,
      futurePrincipal,
      futureInterest,
      equityPercentage,
    });
  }

  calculateMonthlyPayment(principal, monthlyRate, totalMonths) {
    if (monthlyRate === 0) return principal / totalMonths;

    const numerator = monthlyRate * Math.pow(1 + monthlyRate, totalMonths);
    const denominator = Math.pow(1 + monthlyRate, totalMonths) - 1;

    return principal * (numerator / denominator);
  }

  calculateRemainingBalance(principal, monthlyRate, totalMonths, month) {
    if (month <= 0) return principal;
    if (month >= totalMonths) return 0;
    if (monthlyRate === 0) return principal * (1 - month / totalMonths);

    const monthlyPayment = this.calculateMonthlyPayment(
      principal,
      monthlyRate,
      totalMonths,
    );
    const pow = Math.pow(1 + monthlyRate, month);

    return principal * pow - (monthlyPayment * (pow - 1)) / monthlyRate;
  }

  generateAmortizationSchedule() {
    const loanAmount = this.parseCurrency(this.loanAmountTarget.value);
    const interestRate = parseFloat(this.interestRateTarget.value);
    const loanTerm = parseInt(this.loanTermTarget.value);
    const startDate = new Date(this.startDateTarget.value);

    const monthlyInterestRate = interestRate / 12 / 100;
    const monthlyPayment = this.calculateMonthlyPayment(
      loanAmount,
      monthlyInterestRate,
      loanTerm,
    );

    const schedule = [];
    let remainingBalance = loanAmount;
    const currentDate = new Date(startDate);

    for (let month = 1; month <= loanTerm; month++) {
      const interest = remainingBalance * monthlyInterestRate;
      const principal = monthlyPayment - interest;
      remainingBalance -= principal;

      if (remainingBalance < 0) {
        remainingBalance = 0;
      }

      schedule.push({
        date: new Date(currentDate),
        principal,
        interest,
        remainingBalance,
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return schedule;
  }

  updateAmortizationTable() {
    const tableBody = this.tableBodyTarget;
    if (!tableBody) return;

    tableBody.innerHTML = "";
    const schedule = this.generateAmortizationSchedule();

    schedule.slice(0, this.maxRowsValue).forEach((payment, index) => {
      tableBody.appendChild(this.createRow(payment, index));
    });

    if (schedule.length > this.maxRowsValue) {
      const viewMoreRow = document.createElement("tr");
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      viewMoreRow.innerHTML = `
        <td colspan="4" class="py-2 text-right">
          <form action="/user_interactions/log_action" method="POST" onsubmit="event.preventDefault(); logAndShowModal(this);">
            <input type="hidden" name="authenticity_token" value="${csrfToken}" />
            <input type="hidden" name="tn_id" value="26" />
            <input type="hidden" name="action_item" value="move_up_calculator_engagement" />
            <button type="submit" class="bg-primary text-white py-2 px-4 font-montserrat rounded">
              View more
            </button>
          </form>
        </td>
      `;
      tableBody.appendChild(viewMoreRow);

      if (!window.logAndShowModal) {
        window.logAndShowModal = function (form) {
          fetch(form.action, {
            method: "POST",
            body: new FormData(form),
          });

          const modal = document.querySelector('[data-modal-target="modal"]');
          if (modal) {
            modal.classList.remove("hidden");
            modal.classList.add("flex");
          }
        };
      }

      const modalTable = document.createElement("table");
      modalTable.className = "w-full";
      modalTable.innerHTML = `
        <thead>
          <tr>
            <th class="border border-gray-300 p-2">Date</th>
            <th class="border border-gray-300 p-2">Principal</th>
            <th class="border border-gray-300 p-2">Interest</th>
            <th class="border border-gray-300 p-2">Remaining Balance</th>
          </tr>
        </thead>
        <tbody>
          ${schedule
            .map(
              (payment, index) => `
            <tr class="${index % 2 === 0 ? "bg-white" : "bg-grayalt"}">
              <td class="border border-gray-300 p-2">${this.formatDate(payment.date)}</td>
              <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.principal)}</td>
              <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.interest)}</td>
              <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.remainingBalance)}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      `;
      this.modalContentTarget.innerHTML = modalTable.outerHTML;
    }
  }

  createRow(payment, index) {
    const row = document.createElement("tr");
    row.className = index % 2 === 0 ? "bg-white" : "bg-grayalt";

    row.innerHTML = `
      <td class="border border-gray-300 p-2">${this.formatDate(payment.date)}</td>
      <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.principal)}</td>
      <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.interest)}</td>
      <td class="border border-gray-300 p-2 text-right">${this.formatCurrency(payment.remainingBalance)}</td>
    `;

    return row;
  }

  initializeCharts() {
    this.charts = this.charts || {};

    this.createChart(
      this.paymentsToDateChartTarget,
      [this.paidTowardsLoan, this.paidTowardsInterest],
      ["Paid towards loan", "Paid towards interest"],
      "paymentsToDateChart",
    );

    this.createChart(
      this.thisMonthChartTarget,
      [this.thisMonthPrincipal, this.thisMonthInterest],
      ["Monthly principal", "Monthly interest"],
      "thisMonthChart",
    );

    this.createChart(
      this.futurePaymentsChartTarget,
      [this.futurePrincipal, this.futureInterest],
      ["Future principal", "Future interest"],
      "futurePaymentsChart",
    );
  }

  updateUI(data) {
    this.updateAllTargets("numberOfPayments", data.loanTerm);
    this.updateAllTargets(
      "monthlyPayment",
      this.formatCurrency(data.monthlyPayment),
    );
    this.updateAllTargets(
      "totalInterest",
      this.formatCurrency(data.totalInterest),
    );
    this.updateAllTargets("totalCost", this.formatCurrency(data.totalCost));
    this.updateAllTargets("payoffDate", this.formatDate(data.payoffDate));

    this.updateAllTargets(
      "paidTowardsLoan",
      this.formatCurrency(data.paidTowardsLoan),
    );
    this.updateAllTargets(
      "paidTowardsInterest",
      this.formatCurrency(data.paidTowardsInterest),
    );

    this.updateAllTargets(
      "thisMonthPrincipal",
      this.formatCurrency(data.thisMonthPrincipal),
    );
    if (this.hasThisMonthInterestAmountTarget) {
      this.updateAllTargets(
        "thisMonthInterestAmount",
        this.formatCurrency(data.thisMonthInterest),
      );
    }

    this.updateAllTargets(
      "futurePrincipal",
      this.formatCurrency(data.futurePrincipal),
    );
    this.updateAllTargets(
      "futureInterest",
      this.formatCurrency(data.futureInterest),
    );
    this.updateAllTargets(
      "equityPercentage",
      data.equityPercentage.toFixed(1) + "%",
    );

    this.updateChart("paymentsToDateChart", [
      data.paidTowardsLoan,
      data.paidTowardsInterest,
    ]);
    if (this.hasThisMonthChartTarget) {
      this.updateChart("thisMonthChart", [
        data.thisMonthPrincipal,
        data.thisMonthInterest,
      ]);
    }
    if (this.hasFuturePaymentsChartTarget) {
      this.updateChart("futurePaymentsChart", [
        data.futurePrincipal,
        data.futureInterest,
      ]);
    }

    this.updateAmortizationTable();
  }

  updateAllTargets(targetName, value) {
    if (this.hasTarget(targetName)) {
      this[`${targetName}Targets`].forEach((element) => {
        element.textContent = value;
      });
    }
  }

  createChart(canvas, data, labels, chartName) {
    const ctx = canvas.getContext("2d");

    const primaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-color-50")
      .trim();
    const primaryColorDark = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-color")
      .trim();

    const chartPlugins = [];

    const options = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };

    if (chartName === "thisMonthChart") {
      data = [...data].reverse();

      options.rotation = 0;
      options.circumference = 360;
    }

    this.charts[chartName] = new Chart(ctx, {
      type: "pie",
      data: {
        labels: chartName === "thisMonthChart" ? [...labels].reverse() : labels,
        datasets: [
          {
            data,
            backgroundColor:
              chartName === "thisMonthChart"
                ? [primaryColorDark, primaryColor]
                : [primaryColor, primaryColorDark],
            borderWidth: 0,
          },
        ],
      },
      options,
      plugins: chartPlugins,
    });
  }

  updateChart(chartName, data) {
    if (!this.charts?.[chartName]) return;

    this.charts[chartName].data.datasets[0].data = data;
    this.charts[chartName].update();
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatDate(date) {
    return date.toLocaleString("en-US", { month: "short", year: "numeric" });
  }

  parseCurrency(value) {
    if (typeof value === "string") {
      return parseFloat(value.replace(/[$,]/g, "")) || 0;
    }
    return value || 0;
  }

  hasTarget(name) {
    return this[`has${name.charAt(0).toUpperCase() + name.slice(1)}Target`];
  }

  disconnect() {
    if (this.charts) {
      Object.values(this.charts).forEach((chart) => {
        if (chart) chart.destroy();
      });
    }
  }
}
